import { create } from 'zustand';
import { Anuncio, FiltrosAnuncio, PaginatedResponse, Favorito, Conversa, Mensagem } from '../types';
import anunciosService from '../services/anuncios';
import toast from 'react-hot-toast';

interface AnuncioState {
  // Anúncios
  anuncios: Anuncio[];
  anuncioAtual: Anuncio | null;
  meusAnuncios: Anuncio[];
  destaques: Anuncio[];
  recentes: Anuncio[];
  
  // Paginação
  total: number;
  pagina: number;
  totalPaginas: number;
  
  // Filtros
  filtros: FiltrosAnuncio;
  
  // Favoritos
  favoritos: Favorito[];
  favoritosIds: Set<string>;
  
  // Chat
  conversas: Conversa[];
  conversaAtual: Conversa | null;
  mensagens: Mensagem[];
  
  // Loading states
  isLoading: boolean;
  isLoadingMais: boolean;
  
  // Actions - Anúncios
  listarAnuncios: (filtros?: FiltrosAnuncio) => Promise<void>;
  carregarMais: () => Promise<void>;
  buscarAnuncio: (id: string) => Promise<void>;
  carregarMeusAnuncios: () => Promise<void>;
  carregarDestaques: () => Promise<void>;
  carregarRecentes: () => Promise<void>;
  setFiltros: (filtros: Partial<FiltrosAnuncio>) => void;
  limparFiltros: () => void;
  
  // Actions - Favoritos
  carregarFavoritos: () => Promise<void>;
  toggleFavorito: (anuncioId: string) => Promise<void>;
  isFavorito: (anuncioId: string) => boolean;
  
  // Actions - Chat
  carregarConversas: () => Promise<void>;
  selecionarConversa: (conversaId: string) => Promise<void>;
  carregarMensagens: (conversaId: string) => Promise<void>;
  enviarMensagem: (conversaId: string, conteudo: string) => Promise<void>;
  iniciarConversa: (anuncioId: string, mensagem: string) => Promise<string | null>;
  
  // Actions - CRUD Anúncio
  pausarAnuncio: (id: string) => Promise<void>;
  ativarAnuncio: (id: string) => Promise<void>;
  deletarAnuncio: (id: string) => Promise<void>;
  marcarVendido: (id: string) => Promise<void>;
}

const filtrosIniciais: FiltrosAnuncio = {
  busca: '',
  categoria: undefined,
  subcategoria: undefined,
  precoMin: undefined,
  precoMax: undefined,
  estado: undefined,
  cidade: undefined,
  ordenar: 'recentes',
  pagina: 1,
  limite: 12,
};

export const useAnuncioStore = create<AnuncioState>((set, get) => ({
  // Initial state
  anuncios: [],
  anuncioAtual: null,
  meusAnuncios: [],
  destaques: [],
  recentes: [],
  total: 0,
  pagina: 1,
  totalPaginas: 0,
  filtros: filtrosIniciais,
  favoritos: [],
  favoritosIds: new Set(),
  conversas: [],
  conversaAtual: null,
  mensagens: [],
  isLoading: false,
  isLoadingMais: false,

  // Anúncios
  listarAnuncios: async (filtros?: FiltrosAnuncio) => {
    set({ isLoading: true });
    try {
      const filtrosAtuais = filtros || get().filtros;
      const response = await anunciosService.listar({ ...filtrosAtuais, pagina: 1 });
      set({
        anuncios: response.data,
        total: response.total,
        pagina: response.pagina,
        totalPaginas: response.totalPaginas,
        filtros: { ...filtrosAtuais, pagina: 1 },
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      toast.error('Erro ao carregar anúncios');
    }
  },

  carregarMais: async () => {
    const { pagina, totalPaginas, filtros, anuncios } = get();
    if (pagina >= totalPaginas) return;

    set({ isLoadingMais: true });
    try {
      const response = await anunciosService.listar({ ...filtros, pagina: pagina + 1 });
      set({
        anuncios: [...anuncios, ...response.data],
        pagina: response.pagina,
        isLoadingMais: false,
      });
    } catch (error) {
      set({ isLoadingMais: false });
      toast.error('Erro ao carregar mais anúncios');
    }
  },

  buscarAnuncio: async (id: string) => {
    set({ isLoading: true, anuncioAtual: null });
    try {
      const anuncio = await anunciosService.buscar(id);
      set({ anuncioAtual: anuncio, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      toast.error('Anúncio não encontrado');
    }
  },

  carregarMeusAnuncios: async () => {
    set({ isLoading: true });
    try {
      const anuncios = await anunciosService.meusAnuncios();
      set({ meusAnuncios: anuncios, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      toast.error('Erro ao carregar seus anúncios');
    }
  },

  carregarDestaques: async () => {
    try {
      const anuncios = await anunciosService.destaques();
      set({ destaques: anuncios });
    } catch (error) {
      console.error('Erro ao carregar destaques:', error);
    }
  },

  carregarRecentes: async () => {
    try {
      const anuncios = await anunciosService.recentes(8);
      set({ recentes: anuncios });
    } catch (error) {
      console.error('Erro ao carregar recentes:', error);
    }
  },

  setFiltros: (novosFiltros: Partial<FiltrosAnuncio>) => {
    set((state) => ({
      filtros: { ...state.filtros, ...novosFiltros },
    }));
  },

  limparFiltros: () => {
    set({ filtros: filtrosIniciais });
  },

  // Favoritos
  carregarFavoritos: async () => {
    try {
      const favoritos = await anunciosService.listarFavoritos();
      const ids = new Set(favoritos.map((f) => f.anuncio.id));
      set({ favoritos, favoritosIds: ids });
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  },

  toggleFavorito: async (anuncioId: string) => {
    const { favoritosIds, favoritos } = get();
    const isFav = favoritosIds.has(anuncioId);

    try {
      if (isFav) {
        await anunciosService.removerFavorito(anuncioId);
        const novosFavoritos = favoritos.filter((f) => f.anuncio.id !== anuncioId);
        const novosIds = new Set(favoritosIds);
        novosIds.delete(anuncioId);
        set({ favoritos: novosFavoritos, favoritosIds: novosIds });
        toast.success('Removido dos favoritos');
      } else {
        const favorito = await anunciosService.adicionarFavorito(anuncioId);
        const novosIds = new Set(favoritosIds);
        novosIds.add(anuncioId);
        set({ favoritos: [...favoritos, favorito], favoritosIds: novosIds });
        toast.success('Adicionado aos favoritos');
      }
    } catch (error) {
      toast.error('Erro ao atualizar favorito');
    }
  },

  isFavorito: (anuncioId: string) => {
    return get().favoritosIds.has(anuncioId);
  },

  // Chat
  carregarConversas: async () => {
    try {
      const conversas = await anunciosService.listarConversas();
      set({ conversas });
    } catch (error) {
      toast.error('Erro ao carregar conversas');
    }
  },

  selecionarConversa: async (conversaId: string) => {
    set({ isLoading: true });
    try {
      const [conversa, mensagens] = await Promise.all([
        anunciosService.buscarConversa(conversaId),
        anunciosService.listarMensagens(conversaId),
      ]);
      await anunciosService.marcarMensagensLidas(conversaId);
      set({
        conversaAtual: conversa,
        mensagens,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      toast.error('Erro ao carregar conversa');
    }
  },

  carregarMensagens: async (conversaId: string) => {
    try {
      const mensagens = await anunciosService.listarMensagens(conversaId);
      set({ mensagens });
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  },

  enviarMensagem: async (conversaId: string, conteudo: string) => {
    try {
      const mensagem = await anunciosService.enviarMensagem(conversaId, conteudo);
      set((state) => ({
        mensagens: [...state.mensagens, mensagem],
      }));
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    }
  },

  iniciarConversa: async (anuncioId: string, mensagem: string) => {
    try {
      const conversa = await anunciosService.iniciarConversa(anuncioId, mensagem);
      set((state) => ({
        conversas: [conversa, ...state.conversas],
      }));
      toast.success('Mensagem enviada!');
      return conversa.id;
    } catch (error) {
      toast.error('Erro ao iniciar conversa');
      return null;
    }
  },

  // CRUD Anúncio
  pausarAnuncio: async (id: string) => {
    try {
      await anunciosService.pausar(id);
      set((state) => ({
        meusAnuncios: state.meusAnuncios.map((a) =>
          a.id === id ? { ...a, status: 'pausado' as const } : a
        ),
      }));
      toast.success('Anúncio pausado');
    } catch (error) {
      toast.error('Erro ao pausar anúncio');
    }
  },

  ativarAnuncio: async (id: string) => {
    try {
      await anunciosService.ativar(id);
      set((state) => ({
        meusAnuncios: state.meusAnuncios.map((a) =>
          a.id === id ? { ...a, status: 'ativo' as const } : a
        ),
      }));
      toast.success('Anúncio ativado');
    } catch (error) {
      toast.error('Erro ao ativar anúncio');
    }
  },

  deletarAnuncio: async (id: string) => {
    try {
      await anunciosService.deletar(id);
      set((state) => ({
        meusAnuncios: state.meusAnuncios.filter((a) => a.id !== id),
      }));
      toast.success('Anúncio excluído');
    } catch (error) {
      toast.error('Erro ao excluir anúncio');
    }
  },

  marcarVendido: async (id: string) => {
    try {
      await anunciosService.marcarVendido(id);
      set((state) => ({
        meusAnuncios: state.meusAnuncios.map((a) =>
          a.id === id ? { ...a, status: 'vendido' as const } : a
        ),
      }));
      toast.success('Anúncio marcado como vendido');
    } catch (error) {
      toast.error('Erro ao atualizar anúncio');
    }
  },
}));

export default useAnuncioStore;
